import * as Yup from 'yup';
import * as AWS from 'aws-sdk/global';

import { useState, forwardRef, useRef, createRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContentText,
  DialogContent,
  Button,
  Slide,
  FormControl
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useAuth } from '../../customContextProviders/AuthContext';
import { AdminLogin, AdminLoginForOtp } from '../../../network/User';
import { parseValidationErrorMessage } from '../../../utils/Utilities';
import { APP_CONSTANTS } from '../../../URLConstants';
import { ConfirmAlertDialog, SnackBarAlert } from '../../HelperComponents';
import { OpsLogin } from '../../../network/Device';
import { encryptStorage } from '../../../CustomStorage';

/* eslint-disable */
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function LoginForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { authenticateSession } = useAuth();

  const recaptchaRef = createRef();

  const [confirmOpenAlertDialog, setConfirmOpenAlertDialog] = useState(false);
  const [confirmAlertDialogMetas, setConfirmAlertDialogMetas] = useState({
    message: '',
    title: '',
    positiveBtnLable: '',
    negativeBtnLable: '',
    type: '',
    action: ''
  });

  const [errorData, setErrorData] = useState({
    visible: false,
    message: '',
    title: ''
  });
  const [additionalData, setAdditionalData] = useState({ token: '' });

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const isCBIorBOMEnvironment =
    process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID === APP_CONSTANTS?.CBI_CLIENT_IDENTIFIER ||
    process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID === APP_CONSTANTS?.BOM_CLIENT_IDENTIFIER;

  // Keep in sync with doLogin() email vs username detection
  const loginEmailShape = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .test('login-identifier', 'Email must be a valid email address', (value) => {
        if (!value) return true;
        if (value.includes('@')) {
          return loginEmailShape.test(value);
        }
        if (isCBIorBOMEnvironment) {
          return /^[a-zA-Z0-9._-]+$/.test(value);
        }
        return false;
      }),
    password: Yup.string().required('Password is required')
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: true
    },
    validationSchema: LoginSchema,
    onSubmit: () => {
      doLogin(formik.values.email, formik.values.password);
    }
  });

  const handleAlertDialogClose = () => {
    setErrorData({ ...errorData, visible: false, message: '' });
  };

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  function doLogin(emailOrUsername, password) {
    const isEmail = loginEmailShape.test(emailOrUsername);

    // Prepare payload
    const payload = {
      [isEmail ? 'email' : 'username']: emailOrUsername,
      password
    };

    // Choose API based on environment
    const loginApi =
      // process.env.REACT_APP_ENVIRONMENT === 'sandbox' ? AdminLoginForOtp :
      AdminLogin;

    loginApi(payload)
      .then((res) => {
        formik.setSubmitting(false);

        // If sandbox → go to OTP page
        // if (process.env.REACT_APP_ENVIRONMENT === 'sandbox') {
        //   navigate('/validateotp', { state: { userEmail: emailOrUsername } });
        //   return;
        // }

        authenticateSession(res?.data);
        navigate('/dashboard/soundboxT', { replace: true });
      })
      .catch((error) => {
        formik.setSubmitting(false);

        if (error?.response?.data?.errors === 'Kindly reset your password') {
          setAdditionalData((prevState) => ({
            ...prevState,
            token: error?.response?.data?.token
          }));
          setConfirmAlertDialogMetas({
            ...confirmAlertDialogMetas,
            message: 'Your password has expired , please update now !',
            title: 'Password Expired',
            positiveBtnLable: 'YES',
            negativeBtnLable: 'NO, CANCEL',
            alertType: 'FAILURE',
            action: 'Kindly reset your password'
          });
          setConfirmOpenAlertDialog(true);
        } else {
          setSnackBarMetas({
            title: parseValidationErrorMessage(error),
            severity: 'error'
          });
          setIsSnackBarAlertOpen(true);
        }
      });
  }

  function handleConfirmDialogPositiveClose(action) {
    switch (action) {
      case 'Kindly reset your password': {
        setConfirmOpenAlertDialog(false);
        navigate(`/resetpassword/${additionalData?.token}`);
        break;
      }

      default: {
        setConfirmOpenAlertDialog(false);
        break;
      }
    }
  }

  function handleConfirmDialogNegativeClose() {
    setConfirmOpenAlertDialog(false);
  }

  return (
    <div>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              type="email"
              autoComplete="username"
              id="login-email"
              label={isCBIorBOMEnvironment ? 'Email or Employee ID' : 'Email'}
              {...getFieldProps('email')}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
            />

            <TextField
              fullWidth
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              id="login-password"
              label="Password"
              {...getFieldProps('password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
            />
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ my: 0.5 }}>
            <Link component={RouterLink} variant="subtitle2" to="/forgetpassword">
              Forgot password?
            </Link>
          </Stack>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{ mt: 2 }}
          >
            Login
          </LoadingButton>
        </Form>
      </FormikProvider>

      <Dialog
        open={errorData?.visible}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => handleAlertDialogClose()}
        aria-describedby="iot_res_alert-dialog-slide-description"
      >
        <DialogTitle>{errorData?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="aiot_res_alert-dialog-slide-description">
            {errorData?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleAlertDialogClose()}>OK</Button>
        </DialogActions>
      </Dialog>

      <ConfirmAlertDialog
        openConfirmAlertDialog={confirmOpenAlertDialog}
        message={confirmAlertDialogMetas?.message}
        title={confirmAlertDialogMetas?.title}
        positiveBtnLable={confirmAlertDialogMetas?.positiveBtnLable}
        negativeBtnLable={confirmAlertDialogMetas?.negativeBtnLable}
        action={confirmAlertDialogMetas?.action}
        handleClose={() => setConfirmOpenAlertDialog(false)}
        handleNegativeClose={() => handleConfirmDialogNegativeClose()}
        handlePositiveClose={() =>
          handleConfirmDialogPositiveClose(confirmAlertDialogMetas?.action)
        }
        type={confirmAlertDialogMetas?.alertType}
      />

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </div>
  );
}
